/**
 * DesktopLayout - Layout professionale per Desktop CON GLASSMORPHISM
 * 
 * Caratteristiche:
 * - Sidebar permanente con navigazione glass
 * - Header glass per home page
 * - Content area con full viewport height
 * - Supporto per layout a 2 colonne (detail pages)
 * - Blob animati di sfondo
 */

import { useState } from 'react';
import { Outlet, useLocation, Link, Navigate } from 'react-router-dom';
import { useAuth, useIsAuthenticated } from '../stores/auth';
import { BookOpen, User, LogOut, Briefcase, Filter, Ship } from 'lucide-react';
import DesktopSidebar from './DesktopSidebar';
import { GlassLayout } from './Glass/GlassLayout';
import { useGlassmorphism } from '../hooks/useGlassmorphism';
import { useScrollFix } from '../hooks/useScrollFix';

// Pagine che usano layout 2 colonne (detail)
const TWO_COLUMN_PAGES = [
  '/boats/',
  '/motors/',
  '/boat/',
  '/motor/',
  '/trades/',
  '/dealers/',
  '/dealer/',
];

// Pagine che non devono avere la sidebar
const NO_SIDEBAR_PAGES = [
  '/login',
  '/register',
  '/share/',
  '/manuale',
  '/portfolio',
];

// Pagine pubbliche che devono avere header e footer (come home)
const PUBLIC_HEADER_PAGES = [
  '/portfolio',
  '/manuale',
];

export default function DesktopLayout() {
  const location = useLocation();
  const { user, logout } = useAuth();
  const isAuthenticated = useIsAuthenticated();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { glass, isGlassEnabled } = useGlassmorphism();
  useScrollFix();

  // Check se è una Pagina di dettaglio (2 colonne)
  const isTwoColumnPage = TWO_COLUMN_PAGES.some(path => 
    location.pathname.includes(path)
  );

  // Check se è una Pagina senza sidebar
  const isNoSidebarPage = NO_SIDEBAR_PAGES.some(path => 
    location.pathname === path || location.pathname.startsWith(path)
  );

  // Check se siamo sulla home
  const isHomePage = location.pathname === '/' || location.pathname === '/home';
  const isAuthPage = location.pathname === '/login' || location.pathname === '/register';
  
  // Check se è una pagina pubblica con header (usa startsWith per /portfolio/:slug)
  const isPublicHeaderPage = location.pathname === '/portfolio' || location.pathname.startsWith('/portfolio');

  // Redirect alla pagina boats se utente autenticato e sulla home
  if (isHomePage && isAuthenticated) {
    return <Navigate to="/boats" replace />;
  }

  const handleLogout = async () => {
    await logout();
  };

  // Per le pagine pubbliche (portfolio, manuale), mostra header leggero con navigazione
  if (isPublicHeaderPage) {
    return (
      <div className={`min-h-screen ${isGlassEnabled ? glass.background : ''}`} style={!isGlassEnabled ? { backgroundColor: 'var(--color-bg-surface)' } : {}}>
        {/* Header leggero fisso */}
        <header className="sticky-top z-50" style={{ backgroundColor: 'var(--color-bg-primary)', borderBottom: '1px solid var(--color-border)' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-14">
              {/* Logo */}
              <div className="flex items-center">
                <Link to="/" className="flex items-center">
                  <img 
                    src={((window as any).BOAT_TRADER_API?.plugin_url || '') + 'sealevel.webp' || '/wp-content/plugins/boat-trader-v3/assets/images/sealevel.webp'}
                    alt="SeaLevel" 
                    className="h-10 w-auto object-contain"
                    width={200}
                    height={40}
                  />
                </Link>
              </div>

              {/* Navigation Links */}
              <div className="flex items-center space-x-6">
                <Link 
                  to="/boats"
                  className="px-3 py-2 font-medium flex items-center gap-1 transition-colors"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  <Ship className="w-4 h-4" />
                  Visualizza Catalogo
                </Link>
                
                <Link 
                  to="/portfolio"
                  className="px-3 py-2 font-medium flex items-center gap-1 transition-colors"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  <Briefcase className="w-4 h-4" />
                  Progetti
                </Link>
                
                <Link 
                  to="/manuale"
                  className="px-3 py-2 font-medium flex items-center gap-1 transition-colors"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  <BookOpen className="w-4 h-4" />
                  Manuale
                </Link>
                
                {isAuthenticated ? (
                  <div className="flex items-center space-x-3">
                    <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                      {user?.is_admin ? 'Admin' : (user?.company_name || 'Utente')}
                    </span>
                    <button
                      onClick={handleLogout}
                      className="px-3 py-2 font-medium flex items-center gap-1 transition-colors"
                      style={{ color: 'var(--color-text-secondary)' }}
                      aria-label="Logout"
                    >
                      <LogOut className="w-4 h-4" aria-hidden="true" />
                      Logout
                    </button>
                  </div>
                ) : (
                  <Link 
                    to="/login"
                    className="px-3 py-2 font-medium flex items-center gap-1 transition-colors"
                    style={{ color: 'var(--color-text-secondary)' }}
                  >
                    <User className="w-4 h-4" />
                    Login
                  </Link>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <Outlet />
      </div>
    );
  }

  // Per la home page, mostra un header leggero con Manuale e Login (NO sidebar)
  if (isHomePage) {
    return (
      <div className={`min-h-screen ${isGlassEnabled ? glass.background : ''}`} style={!isGlassEnabled ? { backgroundColor: 'var(--color-bg-surface)' } : {}}>
        {/* Header leggero fisso */}
        <header className="sticky-top z-50" style={{ backgroundColor: 'var(--color-bg-primary)', borderBottom: '1px solid var(--color-border)' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-14">
              {/* Logo */}
              <div className="flex items-center">
                <Link to="/" className="flex items-center">
                  <img 
                    src={((window as any).BOAT_TRADER_API?.plugin_url || '') + 'sealevel.webp' || '/wp-content/plugins/boat-trader-v3/assets/images/sealevel.webp'}
                    alt="SeaLevel" 
                    className="h-10 w-auto object-contain"
                    width={200}
                    height={40}
                  />
                </Link>
              </div>

              {/* Navigation Links */}
              <div className="flex items-center space-x-6">
                <Link 
                  to="/boats"
                  className="px-3 py-2 font-medium flex items-center gap-1 transition-colors"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  <Ship className="w-4 h-4" />
                  Visualizza Catalogo
                </Link>
                
                <Link 
                  to="/portfolio"
                  className="px-3 py-2 font-medium flex items-center gap-1 transition-colors"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  <Briefcase className="w-4 h-4" />
                  Progetti
                </Link>
                
                <Link 
                  to="/manuale"
                  className="px-3 py-2 font-medium flex items-center gap-1 transition-colors"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  <BookOpen className="w-4 h-4" />
                  Manuale
                </Link>
                
                {isAuthenticated ? (
                  <div className="flex items-center space-x-3">
                    <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                      {user?.is_admin ? 'Admin' : (user?.company_name || 'Utente')}
                    </span>
                    <button
                      onClick={handleLogout}
                      className="px-3 py-2 font-medium flex items-center gap-1 transition-colors"
                      style={{ color: 'var(--color-text-secondary)' }}
                      aria-label="Logout"
                    >
                      <LogOut className="w-4 h-4" aria-hidden="true" />
                      Logout
                    </button>
                  </div>
                ) : (
                  <Link 
                    to="/login"
                    className="px-3 py-2 font-medium flex items-center gap-1 transition-colors"
                    style={{ color: 'var(--color-text-secondary)' }}
                  >
                    <User className="w-4 h-4" />
                    Login
                  </Link>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <Outlet />
        
        {/* Footer */}
        <footer className="bg-slate-900 text-white py-8 mt-auto">
          <div className="max-w-6xl mx-auto px-4 text-center">
            <div className="flex flex-wrap justify-center gap-6 mb-4">
              <Link to="/chi-siamo" className="text-orange-400 hover:underline">Chi Siamo</Link>
              <Link to="/contatti" className="text-orange-400 hover:underline">Contatti</Link>
              <Link to="/portfolio" className="text-orange-400 hover:underline">I Nostri Progetti</Link>
              <Link to="/manuale" className="text-orange-400 hover:underline">Manuale</Link>
              {!isAuthenticated && <Link to="/login" className="text-orange-400 hover:underline">Login</Link>}
            </div>
            <p className="text-slate-400 text-sm">© 2026 Sea Level. Tutti i diritti riservati.</p>
          </div>
        </footer>
      </div>
    );
  }

  // Se è una Pagina auth
  if (isAuthPage) {
    return <Outlet />;
  }

  // Se è una Pagina senza sidebar (es. manuale)
  if (isNoSidebarPage) {
    return (
      <GlassLayout>
        <Outlet />
      </GlassLayout>
    );
  }

  return (
    <GlassLayout>
      <div className="h-screen flex overflow-hidden gap-3">
        {/* Sidebar con stile glass - scroll verticale */}
        <DesktopSidebar 
          isCollapsed={sidebarCollapsed} 
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        />

        {/* Main Content Area - full viewport, no scroll on container */}
        <main className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden">
          {/* Page Content - fills viewport */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden">
            {isTwoColumnPage ? (
              // Layout 2 colonne per detail pages
              <div className="h-full">
                <Outlet />
              </div>
            ) : (
              // Layout standard
              <div className="max-w-[1800px] mx-auto pb-6 h-full">
                <Outlet />
              </div>
            )}
          </div>
        </main>
      </div>
    </GlassLayout>
  );
}
